import React, { useState, useEffect } from "react";
import { FiX, FiSave, FiClock, FiCheckCircle, FiAlertCircle, FiInfo } from "react-icons/fi";
import { userRequest } from "../utils/requestMethods";
import { toast } from "react-toastify";

// Staff clock-in settings, opened from the Teacher Attendance page. Student gate settings live in
// ClockInOutSettingsModal on Gate Monitoring; each modal saves only its own part, and the backend
// keeps the other part as it is.
const StaffClockInSettingsModal = ({ isOpen, onClose, schoolId, refreshSchool }) => {
  const [loading, setLoading] = useState(false);
  const [fetchingSettings, setFetchingSettings] = useState(false);
  const [settings, setSettings] = useState({
    staff_lateness_penalty_enabled: false,
    staff_scheduled_clock_in_hour: 8,
    staff_scheduled_clock_in_minute: 0,
    staff_penalty_amount: 1000,
  });

  useEffect(() => {
    if (isOpen) {
      // Fetch fresh settings each time modal opens
      fetchCurrentSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const fetchCurrentSettings = async () => {
    try {
      setFetchingSettings(true);
      const { data } = await userRequest.get(`/schools/${schoolId}`);
      const currentStaffSettings = data?.settings?.clock_in?.staff?.lateness_penalty;

      setSettings({
        staff_lateness_penalty_enabled: currentStaffSettings?.enabled || false,
        staff_scheduled_clock_in_hour: currentStaffSettings?.scheduled_clock_in_hour ?? 8,
        staff_scheduled_clock_in_minute: currentStaffSettings?.scheduled_clock_in_minute ?? 0,
        staff_penalty_amount: currentStaffSettings?.penalty_amount ?? 1000,
      });
    } catch (error) {
      console.error("Error fetching current settings:", error);
      toast.error("Failed to load current settings");
    } finally {
      setFetchingSettings(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setLoading(true);

      const payload = {
        staff: {
          lateness_penalty: {
            enabled: settings.staff_lateness_penalty_enabled,
            scheduled_clock_in_hour: parseInt(settings.staff_scheduled_clock_in_hour, 10) || 0,
            scheduled_clock_in_minute: parseInt(settings.staff_scheduled_clock_in_minute, 10) || 0,
            penalty_amount: Number(settings.staff_penalty_amount) || 0,
          },
        },
      };

      await userRequest.patch(`/schools/${schoolId}/settings/clock-in`, payload);

      toast.success("Staff attendance settings updated successfully!");

      if (refreshSchool) {
        await refreshSchool();
      }

      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      console.error("Error updating staff clock-in settings:", error);
      toast.error(error.response?.data?.message || error.response?.data || "Failed to update settings");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const formattedScheduledTime = `${String(settings.staff_scheduled_clock_in_hour).padStart(2, "0")}:${String(
    settings.staff_scheduled_clock_in_minute,
  ).padStart(2, "0")}`;

  const formatAmPm = (hour, minute) => {
    const h = parseInt(hour, 10) || 0;
    const m = parseInt(minute, 10) || 0;
    const period = h >= 12 ? "PM" : "AM";
    const displayHour = h % 12 === 0 ? 12 : h % 12;
    return `${displayHour}:${String(m).padStart(2, "0")} ${period}`;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-2xl flex flex-col max-h-[90vh] shadow-2xl">
        {/* Header — sticky */}
        <div className="bg-slate-50 border-b border-slate-200 px-5 py-4 flex-shrink-0">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-slate-200 rounded-lg flex items-center justify-center flex-shrink-0">
                <FiClock className="w-4 h-4" />
              </div>
              <div>
                <h2 className="text-base md:text-xl font-bold text-slate-800">
                  Staff Attendance Settings
                </h2>
                <p className="text-slate-500 text-xs md:text-sm">
                  Configure staff lateness and absence preferences
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-slate-500 hover:bg-slate-200 p-2 rounded-lg transition-colors flex-shrink-0"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content — scrollable */}
        <div className="p-5 space-y-4 overflow-y-auto flex-1">
          {fetchingSettings ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-indigo-200 border-t-indigo-600 mx-auto mb-4"></div>
                <p className="text-slate-600 text-sm">Loading current settings...</p>
              </div>
            </div>
          ) : (
            <>
              {/* Staff Lateness Penalty */}
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-5">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <FiClock className="w-5 h-5 text-indigo-600" />
                      <h3 className="font-semibold text-slate-800">
                        Staff Lateness & Absence Penalty
                      </h3>
                    </div>
                    <p className="text-sm text-slate-600">
                      Automatically record payroll deductions when staff clock in late or fail to
                      clock in on school days
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      setSettings((prev) => ({
                        ...prev,
                        staff_lateness_penalty_enabled: !prev.staff_lateness_penalty_enabled,
                      }))
                    }
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                      settings.staff_lateness_penalty_enabled ? "bg-indigo-600" : "bg-gray-200"
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        settings.staff_lateness_penalty_enabled ? "translate-x-6" : "translate-x-1"
                      }`}
                    />
                  </button>
                </div>

                {/* Staff Penalty Configuration Fields */}
                {settings.staff_lateness_penalty_enabled && (
                  <div className="mt-4 pt-4 border-t border-slate-200 space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Scheduled Clock-in Cutoff (24h format)
                        </label>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <input
                              type="number"
                              min="0"
                              max="23"
                              placeholder="Hour (0-23)"
                              value={settings.staff_scheduled_clock_in_hour}
                              onChange={(e) =>
                                setSettings((prev) => ({
                                  ...prev,
                                  staff_scheduled_clock_in_hour: Math.min(
                                    23,
                                    Math.max(0, parseInt(e.target.value, 10) || 0),
                                  ),
                                }))
                              }
                              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                            />
                            <span className="text-[11px] text-slate-400 block mt-0.5">
                              Hour (0-23)
                            </span>
                          </div>
                          <div>
                            <input
                              type="number"
                              min="0"
                              max="59"
                              placeholder="Minute (0-59)"
                              value={settings.staff_scheduled_clock_in_minute}
                              onChange={(e) =>
                                setSettings((prev) => ({
                                  ...prev,
                                  staff_scheduled_clock_in_minute: Math.min(
                                    59,
                                    Math.max(0, parseInt(e.target.value, 10) || 0),
                                  ),
                                }))
                              }
                              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                            />
                            <span className="text-[11px] text-slate-400 block mt-0.5">
                              Minute (0-59)
                            </span>
                          </div>
                        </div>
                        <p className="mt-1 text-xs text-indigo-600 font-medium">
                          Cutoff: {formattedScheduledTime} (
                          {formatAmPm(
                            settings.staff_scheduled_clock_in_hour,
                            settings.staff_scheduled_clock_in_minute,
                          )}
                          )
                        </p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Penalty Amount per Late / Absent Day (₦)
                        </label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-medium text-sm">
                            ₦
                          </span>
                          <input
                            type="number"
                            min="0"
                            step="100"
                            placeholder="1000"
                            value={settings.staff_penalty_amount}
                            onChange={(e) =>
                              setSettings((prev) => ({
                                ...prev,
                                staff_penalty_amount: Math.max(0, parseFloat(e.target.value) || 0),
                              }))
                            }
                            className="w-full pl-8 pr-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                          />
                        </div>
                        <p className="mt-1 text-xs text-slate-500">
                          Deducted automatically in the staff member's next payroll batch
                        </p>
                      </div>
                    </div>

                    {/* Behavioral Notices */}
                    <div className="space-y-2">
                      <div className="flex items-start gap-2 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg p-3 text-xs">
                        <FiCheckCircle className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                        <span>
                          <strong>1-Minute Grace Period:</strong> A 1-minute grace period is applied
                          automatically (e.g. clocking in at 08:01 for an 08:00 cutoff is never
                          penalized).
                        </span>
                      </div>

                      <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-3 text-xs">
                        <FiInfo className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                        <span>
                          <strong>Nightly Absence Sweep:</strong> Absence (no clock-in at all on a
                          school day) is also penalized via a nightly sweep.
                        </span>
                      </div>

                      <div className="flex items-start gap-2 bg-slate-100 border border-slate-200 text-slate-600 rounded-lg p-3 text-xs">
                        <FiAlertCircle className="w-4 h-4 text-slate-500 shrink-0 mt-0.5" />
                        <span>
                          <strong>Prospective Only:</strong> Changes apply to future clock-ins only
                          — already-recorded penalties keep their original snapshot amount.
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Settings Summary */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <h4 className="font-semibold text-blue-900 text-sm mb-3">Current Configuration</h4>
                <ul className="space-y-2 text-sm text-blue-800">
                  <li className="flex items-center gap-2">
                    <span
                      className={`w-2 h-2 rounded-full ${settings.staff_lateness_penalty_enabled ? "bg-green-500" : "bg-gray-400"}`}
                    ></span>
                    Staff lateness & absence penalty:{" "}
                    {settings.staff_lateness_penalty_enabled
                      ? `Enabled (₦${Number(settings.staff_penalty_amount).toLocaleString()} after ${formattedScheduledTime})`
                      : "Disabled"}
                  </li>
                </ul>
              </div>
            </>
          )}
        </div>

        {/* Footer — sticky */}
        <div className="px-5 py-4 bg-slate-50 border-t border-slate-200 flex justify-end gap-3 flex-shrink-0">
          <button
            onClick={onClose}
            disabled={loading || fetchingSettings}
            className="px-6 py-2.5 bg-white border border-slate-300 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed text-slate-700 font-medium rounded-full transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSaveSettings}
            disabled={loading || fetchingSettings}
            className="inline-flex items-center px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 disabled:cursor-not-allowed text-white font-medium rounded-full transition-colors shadow-sm"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                Saving...
              </>
            ) : (
              <>
                <FiSave className="w-4 h-4 mr-2" />
                Save Settings
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StaffClockInSettingsModal;
